import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import type { ClaimTask } from '@/api/generated/client';
import type { TaskClaimSummary } from '@/lib/task-filters';

interface CreateTaskData {
  claimId: number;
  title: string;
  description?: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate?: Date;
  assignedTo?: number;
}

export function useAllTasks() {
  const { firebaseUser, user } = useAuth();
  const currentUserId = user?.id;
  const isUserContextPending = !!firebaseUser && currentUserId === undefined;
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedDate, setSelectedDate] = useState<Date>();
  const PAGE_SIZE = 250;
  // Upper bound on pages we will walk before treating pagination as broken.
  // Exceeding this throws (surfacing an error) rather than silently returning a
  // partial list, so the user can retry/report instead of seeing missing data.
  const MAX_PAGES = 100;

  const { data: tasksResponse, isLoading: isLoadingTasks, isError, error, refetch } = useQuery({
    queryKey: ['tasks', 'all'],
    queryFn: async () => {
      const allTasks: ClaimTask[] = [];
      let page = 1;
      let hasMore = true;

      while (hasMore) {
        // Fail loudly rather than silently returning a partial list if pagination
        // never terminates (inconsistent metadata or an unexpectedly large dataset).
        // Checked before fetching so exactly MAX_PAGES pages are allowed and we only
        // error when about to request page MAX_PAGES + 1.
        if (page > MAX_PAGES) {
          throw new Error(
            `Global task pagination exceeded ${MAX_PAGES} pages; aborting to avoid showing an incomplete task list.`,
          );
        }

        const response = await apiClient.tasks.getAllTasks({
          includeCompleted: true,
          limit: PAGE_SIZE,
          page,
        });

        allTasks.push(...(response.items || []));
        hasMore = !!response.hasMore;
        page += 1;
      }

      return { items: allTasks };
    },
    enabled: !!firebaseUser,
  });

  const tasks = isUserContextPending ? [] : (tasksResponse?.items || []);
  const claimsMap = new Map<number, TaskClaimSummary>();
  for (const task of tasks) {
    if (claimsMap.has(task.claimId)) continue;
    claimsMap.set(task.claimId, {
      id: task.claimId,
      claimNumber: task.claimNumber ?? null,
      insuredName: task.insuredName ?? null,
      canWrite: task.canWrite ?? false,
    });
  }
  const isLoading = isLoadingTasks || isUserContextPending;

  const handleCreateTask = async (taskData: CreateTaskData) => {
    if (currentUserId === undefined) {
      toast({
        title: 'Permissions are loading',
        description: 'Please wait for your account access to finish loading before creating tasks.',
        variant: 'destructive',
      });
      return;
    }

    // claimsMap is derived only from already-loaded tasks, so a claim with no
    // tasks yet (the user is creating their first one) won't have an entry here.
    // Only block when we positively know the claim is read-only — an absent entry
    // means "unknown", not "read-only", and should not be treated as a refusal.
    // (The create-task UI already restricts claim selection to writable claims
    // using the authoritative claims list, and the backend enforces permission
    // regardless.)
    const claim = claimsMap.get(taskData.claimId);

    if (claim && !claim.canWrite) {
      toast({
        title: 'Read-only claim',
        description: 'You cannot create tasks for a claim shared as read-only.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await apiClient.tasks.createClaimTask({
        claimId: taskData.claimId,
        createClaimTaskRequest: {
          title: taskData.title,
          description: taskData.description,
          priority: taskData.priority,
          dueDate: taskData.dueDate,
          assignedTo: taskData.assignedTo,
        },
      });
      
      toast({
        title: 'Success',
        description: 'Task created successfully',
      });
      
      refetch();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to create task',
        variant: 'destructive',
      });
    }
  };

  const handleCompleteTask = async (taskId: number, claimId: number) => {
    if (currentUserId === undefined) {
      toast({
        title: 'Permissions are loading',
        description: 'Please wait for your account access to finish loading before completing tasks.',
        variant: 'destructive',
      });
      return;
    }

    const claim = claimsMap.get(claimId);
    const canWrite = claim?.canWrite ?? false;

    if (!canWrite) {
      toast({
        title: 'Read-only claim',
        description: 'You cannot complete tasks for a claim shared as read-only.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await apiClient.tasks.completeClaimTask({
        claimId,
        id: taskId,
      });
      
      toast({
        title: 'Success',
        description: 'Task marked as complete',
      });
      
      refetch();
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to complete task',
        variant: 'destructive',
      });
    }
  };

  return {
    tasks,
    claimsMap,
    isLoading,
    isError,
    error,
    refetch,
    selectedDate,
    setSelectedDate,
    handleCreateTask,
    handleCompleteTask,
  };
}
